import { CacheStrategy } from '@rammetec/data-access';
import { AUDIT_FIELD_DESCRIPTORS, AUDIT_FOREIGN_KEYS } from './_auditFields.js';
import { BUSINESS_ALLOWED_DATA_SOURCES, BUSINESS_DEFAULT_DATA_SOURCE } from './dataSourcePolicy.js';
import { PRODUCTION_ORDER_MATERIAL, } from '../types/production-order-material.js';
import { ConsumptionTypeValues } from '../types/technical-sheet-material.js';
import { ProductionOrderLineSourceValues, PRODUCTION_ORDER_LINE_SOURCE_LABELS } from '../types/production-order-step.js';
/**
 * Descriptor for ProductionOrderMaterial (planned inputs under an OP).
 * Subcollection: org/{orgId}/production-order/{productionOrderId}/material
 *
 * Resolve repository with context: { orgId, productionOrderId }.
 * `unitId` is intentionally absent — Product exposes `unit` as a code, not a Unit FK.
 */
export const productionOrderMaterialDescriptor = {
    name: PRODUCTION_ORDER_MATERIAL,
    label: 'Insumo da OP',
    domain: 'production',
    collectionPath: 'org/{orgId}/production-order/{productionOrderId}/material',
    pathPatternKeys: {
        orgId: 'string',
        productionOrderId: 'string',
    },
    version: 1,
    description: 'Planned material of a production order — snapshot at release (spec 0040 §2.4)',
    repositoryConfig: {
        dataSource: BUSINESS_DEFAULT_DATA_SOURCE,
        // NoCache: filho de entidade versionada (spec 0045) — itens em cache voltariam velhos após Recarregar e seriam regravados por cima.
        cache: CacheStrategy.NoCache,
    },
    allowedDataSources: BUSINESS_ALLOWED_DATA_SOURCES,
    fields: [
        { name: 'id', label: 'ID', type: 'string', required: true },
        {
            name: 'productionOrderId',
            label: 'Ordem de produção',
            type: 'string',
            required: true,
            description: 'Parent production order id (also encoded in the path)',
        },
        {
            name: 'componentId',
            label: 'Componente',
            type: 'string',
            required: false,
            description: 'Component copied from the sheet input (nullable on a manual row)',
        },
        {
            name: 'productId',
            label: 'Produto',
            type: 'string',
            required: true,
            description: 'ProductRef.productId (anchor)',
        },
        {
            name: 'variantId',
            label: 'Variante',
            type: 'string',
            required: false,
            description: 'Partial ProductVariant (identity level), same as TechnicalSheetMaterial',
        },
        {
            name: 'processId',
            label: 'Processo',
            type: 'string',
            required: false,
            description: 'Process this input is consumed in (CalculatedInput.processId)',
        },
        {
            name: 'quantity',
            example: 1.05,
            label: 'Quantidade (ficha)',
            type: 'number',
            required: true,
            description: 'Raw sheet quantity (CalculatedInput.quantity) — not scaled',
        },
        {
            name: 'consumptionType',
            example: 'Linear',
            label: 'Tipo de consumo',
            type: 'enum',
            required: true,
            enumValues: [...ConsumptionTypeValues],
        },
        {
            name: 'quantityPerBatchOrFixed',
            example: 50,
            label: 'Tamanho do lote / valor fixo',
            type: 'number',
            required: false,
            description: 'Lote: batch size; Fixo: fixed total. Required when type is Lote or Fixo',
        },
        {
            name: 'plannedQuantity',
            example: 262.5,
            label: 'Quantidade planejada',
            type: 'number',
            required: true,
            description: 'Scaled quantity at release (effectiveQuantity). Manual rows: typed. Not recalculated if header.quantity changes.',
        },
        {
            name: 'source',
            example: 'sheet',
            label: 'Origem',
            type: 'enum',
            required: true,
            enumValues: [...ProductionOrderLineSourceValues],
            enumLabels: PRODUCTION_ORDER_LINE_SOURCE_LABELS,
            description: "Provenance: 'sheet' (release snapshot) or 'manual'",
        },
        ...AUDIT_FIELD_DESCRIPTORS,
    ],
    foreignKeys: [
        {
            fieldName: 'componentId',
            targetEntity: 'Component',
            relationship: 'many-to-one',
            displayField: 'description',
            description: 'Componente do insumo',
        },
        {
            fieldName: 'productId',
            targetEntity: 'Product',
            relationship: 'many-to-one',
            displayField: 'description',
            description: 'Produto do insumo',
        },
        {
            fieldName: 'variantId',
            targetEntity: 'ProductVariant',
            relationship: 'many-to-one',
            displayField: 'description',
            description: 'Variante do insumo (identidade)',
        },
        {
            fieldName: 'processId',
            targetEntity: 'Process',
            relationship: 'many-to-one',
            displayField: 'description',
            description: 'Processo em que o insumo é consumido',
        },
        {
            fieldName: 'productionOrderId',
            targetEntity: 'ProductionOrder',
            relationship: 'many-to-one',
            displayField: 'code',
            description: 'Ordem de produção pai',
        },
        ...AUDIT_FOREIGN_KEYS,
    ],
};
//# sourceMappingURL=production-order-material.descriptor.js.map